import React, { useEffect, useState } from 'react';
import { IActivityReportState, IAdditionalFilter, IDataField, IFileInfo, IDataOption, ReportType } from '../../types/reportsTypes';
import { AdditionalFilters } from '../../components/additional-filters/AdditionalFilters';
import { DataSelection } from '../../components/data-selection/DataSelection';
import { DataPreview } from '../../components/data-preview/DataPreview';
import { Divider } from '@heroui/react';
import { CalendarDate } from '@internationalized/date';
import { useAppSelector, useAppDispatch } from '../../../../core/rootHooks';
import { selectSelectedVesselsImoNumbers, selectSelectedVesselsSectors } from '../../../../shared/sharedSelectors';
import { calculateDataExportEstimation } from '../../utils/downloadHelpers';
import { TReportRequestParams } from '../../types/downloadTypes';
import { activityReportActions } from '../../slices/activityReportSlice';
import { selectActivityReportLoading } from '../../slices/activityReportSlice';
import { columnMappingsActions, selectColumnMappings, selectColumnMappingsLoading } from '../../slices/columnMappingsSlice';
import { selectGeneralSeaAreas } from '../../slices/generalSeaAreasSlice';
import {
	areaSignalStatisticsActions,
	selectAreaSignalStatistics,
	selectAreaSignalStatisticsLoading,
} from '../../slices/areaSignalStatisticsSlice';
import { formatDateAsString } from '../../../../utils/helpers';
import { selectDateRangeBoundaries } from '../../selectors/downloadSelectors';
import { addSeascapeLogActivity } from '../../../../activity-logging/activityLogApi';
import { SeascapeActivityLogConstants } from '../../../../activity-logging/activityLogTypes';


const ActivityReport: React.FC = () => {
	const { firstDayOfGivenMonthOrLastMonth, lastDayOfGivenMonthOrLastMonth } = useAppSelector(selectDateRangeBoundaries);

	// Initialize state with dynamic values based on dateRange
	const getInitialState = (): IActivityReportState => ({
		additionalFilters: {
			imoNumbers: [],
			sectors: [],
			dateRange: {
				start: new CalendarDate(
					firstDayOfGivenMonthOrLastMonth.getFullYear(),
					firstDayOfGivenMonthOrLastMonth.getMonth() + 1,
					firstDayOfGivenMonthOrLastMonth.getDate(),
				),
				end: new CalendarDate(
					lastDayOfGivenMonthOrLastMonth.getFullYear(),
					lastDayOfGivenMonthOrLastMonth.getMonth() + 1,
					lastDayOfGivenMonthOrLastMonth.getDate(),
				),
			},
			location: {
				type: 'all',
				coordinates: {
					lat: null,
					lon: null,
					latTo: null,
					lonTo: null,
				},
				areaId: [],
			},
		},
		dataFields: [], // Will be populated from API
		dataOptions: {
			frequency: 'hourly',
		},
		fileInfo: {
			recordCount: 0,
			vesselCount: 0,
			firstRecord: '-',
			lastRecord: '-',
			estimatedSize: '0.00 MB',
			fileType: 'parquet',
		},
	});

	const [state, setState] = useState<IActivityReportState>(getInitialState());
	const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
	const [areaPercent, setAreaPercent] = useState<any>(null);

	const selectedVesselsImoNumbers = useAppSelector(selectSelectedVesselsImoNumbers);
	const selectedVesselsSectors = useAppSelector(selectSelectedVesselsSectors);
	const columnMappingsData = useAppSelector(selectColumnMappings);
	const columnMappingsLoading = useAppSelector(selectColumnMappingsLoading);
	const generalSeaAreas = useAppSelector(selectGeneralSeaAreas);
	const areaSignalStatistics = useAppSelector(selectAreaSignalStatistics);
	const areaSignalStatisticsLoading = useAppSelector(selectAreaSignalStatisticsLoading);
	const dispatch = useAppDispatch();

	const activityReportLoading = useAppSelector(selectActivityReportLoading);

	// Fetch column mappings on component mount
	useEffect(() => {
		dispatch(columnMappingsActions.getColumnMappingsRequestAction());
	}, [dispatch]);

	// Update dataFields when column mappings data is available
	useEffect(() => {
		if (columnMappingsData && columnMappingsData.length > 0) {
			const mappedDataFields: IDataField[] = columnMappingsData.map((mapping) => ({
				id: mapping.databricksColumnName,
				label: mapping.uiName,
				category: mapping.groupName,
				selected: false,
				columnSizeCsv: mapping.columnSizeCsv,
				order: mapping.order,
				groupOrder: mapping.groupOrder,
			}));

			setState((prev) => ({
				...prev,
				dataFields: mappedDataFields,
			}));
		}
	}, [columnMappingsData]);

	// Set up event listener for the Load button
	useEffect(() => {
		const handleLoadEvent = () => {
			handleLoadData();
		};

		window.addEventListener('activity-report:load', handleLoadEvent);

		return () => {
			window.removeEventListener('activity-report:load', handleLoadEvent);
		};
	}, [state]); // Re-add event listener when state changes to capture latest state

	// Handle area signal statistics response and recalculate estimation
	useEffect(() => {
		if (
			state.additionalFilters.location!.type === 'latlong' &&
			state.additionalFilters.location!.coordinates &&
			areaSignalStatistics &&
			areaSignalStatistics.percent &&
			areaPercent !== areaSignalStatistics.percent
		) {
			// Calculate adjusted vessel count based on the percentage from area signal statistics
			const adjustedVesselCount = Math.ceil(selectedVesselsImoNumbers.length * (areaSignalStatistics.percent / 100));

			const exportEstimation = calculateEstimationWithAdjustments(adjustedVesselCount);

			setAreaPercent(areaSignalStatistics.percent); // Update area percent state

			setState((prev) => ({
				...prev,
				fileInfo: {
					...prev.fileInfo,
					vesselCount: selectedVesselsImoNumbers.length,
					estimatedSize: exportEstimation.estimatedSize,
					recordCount: exportEstimation.estimatedRecords,
				},
			}));
		}
	}, [
		areaSignalStatistics,
		selectedVesselsImoNumbers.length,
		state.dataFields,
		state.additionalFilters.dateRange,
		state.dataOptions.frequency,
		state.fileInfo.fileType,
		state.additionalFilters.location,
		generalSeaAreas,
	]);

	// Handler functions for state updates
	const handleAdditionalFiltersChange = (filters: IAdditionalFilter) => {
		if (filters.location!.type === 'latlong' && !filters.location!.coordinates) {
			// If location type is latlong but no coordinates are provided, reset the coordinates
			filters.location!.coordinates = {
				lat: -90,
				latTo: 90,
				lon: -180,
				lonTo: 180,
			};
		}

		setState((prev) => ({
			...prev,
			additionalFilters: filters,
		}));
		setIsDataLoaded(false); // Reset data loaded state when filters change
	};

	const handleDataFieldsChange = (dataFields: IDataField[]) => {
		setState((prev) => ({
			...prev,
			dataFields,
		}));
		setIsDataLoaded(false); // Reset data loaded state when data fields change
	};

	const handleDataOptionsChange = (dataOptions: IDataOption) => {
		setState((prev) => ({
			...prev,
			dataOptions,
		}));
		setIsDataLoaded(false); // Reset data loaded state when data options change
	};

	const handleFileInfoChange = (fileInfo: IFileInfo) => {
		setState((prev) => ({
			...prev,
			fileInfo,
		}));

		// If location type is latlong, dispatch area signal statistics request to ensure we have updated data
		if (state.additionalFilters.location!.type === 'latlong' && state.additionalFilters.location!.coordinates) {
			const { lat, lon, latTo, lonTo } = state.additionalFilters.location!.coordinates;

			// Ensure all coordinates are provided and are numbers
			if (
				typeof lat === 'number' &&
				typeof lon === 'number' &&
				typeof latTo === 'number' &&
				typeof lonTo === 'number' &&
				lat !== latTo &&
				lon !== lonTo &&
				!isNaN(lat) &&
				!isNaN(lon) &&
				!isNaN(latTo) &&
				!isNaN(lonTo)
			) {
				// Dispatch area signal statistics request
				dispatch(
					areaSignalStatisticsActions.getAreaSignalStatisticsRequestAction({
						lat,
						latTo,
						lon,
						lonTo,
					}),
				);
			}
		}

		const adjustedVesselCount = calculateAdjustedVesselCount();
		const exportEstimation = calculateEstimationWithAdjustments(
			adjustedVesselCount, // use calculated vessel count
			undefined, // use current date range
			undefined, // use current data fields
			undefined, // use current frequency
			fileInfo.fileType,
		);

		setState((prev) => ({
			...prev,
			fileInfo: {
				...fileInfo,
				estimatedSize: exportEstimation.estimatedSize,
				recordCount: exportEstimation.estimatedRecords,
			},
		}));
	};

	const handleDownload = () => {
		// Create activity report request params from the current state
		// Set end date to end of day (23:59:59) for proper range coverage
		const endDate = state.additionalFilters.dateRange!.end.toDate('UTC');
		endDate.setUTCHours(23, 59, 59, 999);
		
		const requestParams: TReportRequestParams = {
			reportType: ReportType.Activity,
			imoList: state.additionalFilters.imoNumbers,
			sectors: state.additionalFilters.sectors!,
			startDate: state.additionalFilters.dateRange!.start.toDate('UTC').toISOString() || '',
			endDate: endDate.toISOString() || '',
			requestedColumns: state.dataFields.filter((field) => field.selected).map((field) => field.id),
			frequency: state.dataOptions.frequency!,
			minLat:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.lat || -90)) ||
				null,
			maxLat:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.latTo || 90)) ||
				null,
			minLong:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.lon || -180)) ||
				null,
			maxLong:
				(state.additionalFilters.location!.type === 'latlong' && (state.additionalFilters.location!.coordinates?.lonTo || 180)) ||
				null,
			seaArea:
				(state.additionalFilters.location!.type === 'seaArea' &&
					state.additionalFilters.location!.areaId?.length &&
					state.additionalFilters.location!.areaId[0]) ||
				'',
			fileType: state.fileInfo.fileType,
		};
				
		const payload = JSON.stringify({ DataType: 'Activity', RequestedParams: requestParams });
	
		addSeascapeLogActivity({ eventId:SeascapeActivityLogConstants.MSI_DATA_DOWNLOAD, payload });

		// Dispatch the request action
		dispatch(activityReportActions.submitActivityReportRequestAction(requestParams));
	};

	const dateFormatOptions: Intl.DateTimeFormatOptions = {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	};

	const calculateAdjustedVesselCount = (locationOverride?: IAdditionalFilter['location']): number => {
		let adjustedVesselCount = selectedVesselsImoNumbers.length;
		const location = locationOverride || state.additionalFilters.location;

		// If location type is seaArea, filter vessel count according to signalPercent
		if (location!.type === 'seaArea' && location!.areaId && generalSeaAreas) {
			const selectedAreaIds = location!.areaId;

			// Find the selected sea areas and calculate sum of signal percentages
			const selectedSeaAreas = generalSeaAreas.filter((area) => selectedAreaIds.includes(area.name));

			if (selectedSeaAreas.length > 0) {
				// Calculate sum of signal percentages across selected areas
				const totalSignalPercent = selectedSeaAreas.reduce((sum, area) => sum + area.signalPercent, 0);

				// Calculate adjusted vessel count based on the sum of signal percentages
				adjustedVesselCount = Math.ceil(selectedVesselsImoNumbers.length * (totalSignalPercent / 100));
			}
		} else if (location!.type === 'latlong' && areaSignalStatistics && areaSignalStatistics.percent) {
			// For latlong, we'll use the response from area signal statistics (handled in useEffect)
			adjustedVesselCount = Math.ceil(selectedVesselsImoNumbers.length * (areaSignalStatistics.percent / 100));
		}

		return adjustedVesselCount;
	};

	const calculateEstimationWithAdjustments = (
		overrideVesselCount?: number,
		dateRangeOverride?: IAdditionalFilter['dateRange'],
		dataFieldsOverride?: IDataField[],
		frequencyOverride?: IDataOption['frequency'],
		fileTypeOverride?: string,
		locationOverride?: IAdditionalFilter['location'],
	): ReturnType<typeof calculateDataExportEstimation> => {
		const vesselCount = overrideVesselCount ?? calculateAdjustedVesselCount(locationOverride);
		return calculateDataExportEstimation(
			vesselCount,
			dataFieldsOverride || state.dataFields,
			dateRangeOverride || state.additionalFilters.dateRange!,
			frequencyOverride || state.dataOptions.frequency!,
			fileTypeOverride || state.fileInfo.fileType,
		);
	};

	const updateVesselData = () => {
		// If location type is latlong, dispatch area signal statistics request
		if (state.additionalFilters.location!.type === 'latlong' && state.additionalFilters.location!.coordinates) {
			const { lat, lon, latTo, lonTo } = state.additionalFilters.location!.coordinates;

			// Ensure all coordinates are provided and are numbers
			if (
				typeof lat === 'number' &&
				typeof lon === 'number' &&
				typeof latTo === 'number' &&
				typeof lonTo === 'number' &&
				lat !== latTo &&
				lon !== lonTo &&
				!isNaN(lat) &&
				!isNaN(lon) &&
				!isNaN(latTo) &&
				!isNaN(lonTo)
			) {
				// Dispatch area
				dispatch(
					areaSignalStatisticsActions.getAreaSignalStatisticsRequestAction({
						lat,
						latTo,
						lon,
						lonTo,
					}),
				);
			} else {
				return;
			}
		}

		const adjustedVesselCount = calculateAdjustedVesselCount();
		const exportEstimation = calculateEstimationWithAdjustments(adjustedVesselCount);

		setState((prev) => ({
			...prev,
			additionalFilters: {
				...prev.additionalFilters,
				imoNumbers: selectedVesselsImoNumbers,
				sectors: selectedVesselsSectors,
			},
			fileInfo: {
				...prev.fileInfo,
				vesselCount: selectedVesselsImoNumbers.length,
				firstRecord: formatDateAsString(state.additionalFilters.dateRange!.start?.toString(), dateFormatOptions) ?? '-',
				lastRecord: formatDateAsString(state.additionalFilters.dateRange!.end?.toString(), dateFormatOptions) ?? '-',
				estimatedSize: exportEstimation.estimatedSize,
				recordCount: exportEstimation.estimatedRecords,
			},
		}));
	};

	const handleLoadData = () => {
		updateVesselData();
		setIsDataLoaded(true);
	};

	return (
		<div className="activity-report-container flex w-full h-full gap-4">
			{/* Left panel: Additional Filters */}
			<div className="w-2/3 h-full">
				<AdditionalFilters
					filters={state.additionalFilters}
					onChange={handleAdditionalFiltersChange}
					maxDate={lastDayOfGivenMonthOrLastMonth}
				/>
			</div>

			<Divider orientation="vertical" />

			{/* Middle panel: Data Selection */}
			<div className="w-full h-full">
				<DataSelection
					dataFields={state.dataFields}
					onDataFieldsChange={handleDataFieldsChange}
					dataOptions={state.dataOptions}
					onDataOptionsChange={handleDataOptionsChange}
				/>
			</div>

			<Divider orientation="vertical" />

			{/* Right panel: Data Preview */}
			<div className="w-2/3 h-full">
				<DataPreview
					fileInfo={state.fileInfo}
					onFileInfoChange={handleFileInfoChange}
					isLoading={activityReportLoading || columnMappingsLoading || areaSignalStatisticsLoading}
					onDownload={handleDownload}
					isDataLoaded={isDataLoaded}
				/>
			</div>
		</div>
	);
};

export default ActivityReport;
